import { Component, Input, Output, EventEmitter } from '@angular/core';

export interface VariableGridRow {
	fields: string[];
	columns: number;
	maxColumns?: number; // specify max columns for this row (for alignment)
}

export interface VariableGridFieldAction {
	label: string;
	icon?: string;
	severity?: 'danger' | 'warning' | 'success' | 'info' | 'secondary';
	visible?: boolean;
	loading?: boolean;
}

export interface VariableGridFieldBadge {
	value: string;
	severity?: 'success' | 'info' | 'warning' | 'danger' | 'secondary' | 'contrast' | undefined;
	styleClass?: string;
}

@Component({
	selector: 'app-variable-grid-card',
	templateUrl: './variable-grid-card.component.html',
	styleUrls: ['./variable-grid-card.component.scss']
})
export class VariableGridCardComponent {
	@Input() title: string = '';
	@Input() info: any;
	@Input() rows: VariableGridRow[] = [];
	@Input() labels: Record<string, { text: string; pipe?: string }> = {};
	@Input() maxColumns: number = 3; // Maximum columns for alignment
	@Input() buttonLabel: string = 'Edit';
	@Input() buttonIcon: string = 'pi-pencil';
	@Input() hideButton: boolean = false;
	@Input() buttonDisabled: boolean = false;
	@Input() buttonLoading: boolean = false;
	@Input() fieldActions: Record<string, VariableGridFieldAction> = {};
	@Input() fieldBadges: Record<string, VariableGridFieldBadge> = {};
	@Input() fieldHelpTexts: Record<string, string | null | undefined> = {};
	@Input() fieldSubvalues: Record<string, string | null | undefined> = {};
	@Input() fieldSubvalueCopyable: Record<string, boolean> = {};
	@Output() buttonClick = new EventEmitter<void>();
	@Output() fieldActionClick = new EventEmitter<string>();

	copiedField: string | null = null;
	private copyResetTimer: ReturnType<typeof setTimeout> | null = null;

	async copySubvalue(field: string): Promise<void> {
		const value = this.fieldSubvalues[field];
		if (!value) return;
		try {
			await navigator.clipboard.writeText(value);
			this.copiedField = field;
			if (this.copyResetTimer) clearTimeout(this.copyResetTimer);
			this.copyResetTimer = setTimeout(() => (this.copiedField = null), 1500);
		} catch {
			// Clipboard API unavailable (e.g. non-secure context) — silently ignore.
		}
	}

	displayValue(value: any): string {
		if (value === null || value === undefined || value === '' || value?.toString().includes('N/A')) {
			return '-';
		}

		if (value === 0) {
			return '0';
		}
		return value.toString();
	}

	getEmptySlots(row: VariableGridRow): number[] {
		const emptyCount = this.maxColumns - row.fields.length;
		return emptyCount > 0
			? Array(emptyCount)
					.fill(0)
					.map((_, i) => i)
			: [];
	}

	trackByRowFields(_index: number, row: VariableGridRow): string {
		return row.fields.join(',');
	}
}
